import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { catsApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';

const AddCatPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [photoUploadStatus, setPhotoUploadStatus] = useState('');
  
  // Handle photo selection
  const handlePhotoChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file size and type
    const validFiles = [];
    const invalidFiles = [];
    
    selectedFiles.forEach(file => {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (file too large, max 5MB)`);
        return;
      }
      
      // Check file type
      if (!file.type.match('image.*')) {
        invalidFiles.push(`${file.name} (not an image file)`);
        return;
      }
      
      validFiles.push(file);
    });
    
    // Check total number of photos
    if (photos.length + validFiles.length > 5) {
      alert('You can upload a maximum of 5 photos per cat.');
      return;
    }
    
    if (invalidFiles.length > 0) {
      alert(`Some files couldn't be added:\n${invalidFiles.join('\n')}`);
    }
    
    if (validFiles.length > 0) {
      setPhotos([...photos, ...validFiles]);
      
      // Create preview URLs
      const newPreviewImages = validFiles.map(file => URL.createObjectURL(file));
      setPreviewImages([...previewImages, ...newPreviewImages]);
      
      setPhotoUploadStatus(`${validFiles.length} photo${validFiles.length > 1 ? 's' : ''} added successfully.`);
      
      // Clear upload status after 3 seconds
      setTimeout(() => {
        setPhotoUploadStatus('');
      }, 3000);
    }
  };
  
  // Remove a photo
  const removePhoto = (index) => {
    const newPhotos = [...photos];
    const newPreviewImages = [...previewImages];
    
    // Revoke the URL to prevent memory leaks
    URL.revokeObjectURL(newPreviewImages[index]);
    
    newPhotos.splice(index, 1);
    newPreviewImages.splice(index, 1);
    
    setPhotos(newPhotos);
    setPreviewImages(newPreviewImages);
  };
  
  // Handle age input (ensure it's a positive integer)
  const handleAgeChange = (e) => {
    const value = e.target.value;
    // Allow empty string or positive integers only
    if (value === '' || /^[0-9]+$/.test(value)) {
      setAge(value);
    }
  };
  
  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handlePhotoChange({ target: { files: e.dataTransfer.files } });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!name || !gender) {
      setError('Name and gender are required fields');
      return;
    }
    
    // Validate age if provided (should be a positive integer)
    if (age && (isNaN(parseInt(age)) || parseInt(age) < 0)) {
      setError('Age must be a positive number of months');
      return;
    }
    
    // Check if at least one photo is provided
    if (photos.length === 0) {
      setError('Please add at least one photo');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', name);
      formData.append('gender', gender);
      if (age) formData.append('age', parseInt(age)); // Convert to integer
      if (description) formData.append('description', description);
      
      // Append all photos to the formData
      photos.forEach((photo) => {
        formData.append('files', photo);
      });
      
      // Add cat via API
      await catsApi.addCat(formData);
      
      // Redirect to cats page with success message
      navigate('/feeder/my-cats', { 
        state: { success: true, message: `${name} has been added successfully!` } 
      });
    } catch (error) {
      console.error('Error adding cat:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="add-cat-page">
      <div className="page-header">
        <h1 className="page-title">Add a New Cat</h1>
        <p className="page-description">
          Add a new cat to your collection to track feedings
        </p>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button className="close-button" onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      <div className="card">
        <form onSubmit={handleSubmit} className="add-cat-form">
          <div className="form-group">
            <label htmlFor="name">Cat Name *</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter cat name"
              required
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="gender">Gender *</label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              required
              className="form-control"
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="age">Age (in months)</label>
            <input
              type="number"
              id="age"
              value={age}
              onChange={handleAgeChange}
              placeholder="Enter age in months"
              min="0"
              className="form-control"
            />
            <small className="helper-text">Please enter the cat's age in months (e.g., 18 for a 1.5-year-old cat)</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the cat (appearance, behavior, location, etc.)"
              className="form-control"
              rows="4"
            ></textarea>
          </div>
          
          <div className="form-group">
            <label>Photos *</label>
            <div 
              className="photo-upload-area"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              
              {photoUploadStatus && (
                <div className="upload-status">
                  {photoUploadStatus}
                </div>
              )}
              
              <div className={`photo-grid ${photos.length === 0 ? 'empty' : ''}`}>
                {/* Drag & drop area when no photos */}
                {photos.length === 0 && (
                  <div 
                    className="drag-drop-area"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <div className="upload-icon">📷</div>
                    <p>Drag & drop photos here or click to browse</p>
                    <p className="small-text">Max 5 photos, 5MB each</p>
                  </div>
                )}
                
                {/* Preview images */}
                {previewImages.map((previewUrl, index) => (
                  <div key={index} className="photo-preview">
                    <img src={previewUrl} alt={`Preview ${index + 1}`} />
                    <button 
                      type="button" 
                      className="remove-photo-btn"
                      onClick={() => removePhoto(index)}
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                
                {/* Add photo button */}
                {photos.length < 5 && photos.length > 0 && (
                  <div 
                    className="add-photo-btn"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <div className="add-icon">+</div>
                    <span>Add Photo</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="form-buttons">
            <button
              type="button"
              onClick={() => navigate('/feeder/my-cats')}
              className="button secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Cat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCatPage; 